import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { action, computed, set } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableItem from 'ember-sortable/modifiers/sortable-item';

type Item<T> = T & {
  isDisabled?: boolean;
  isHovering?: boolean;
};

interface GeneralModalsModalSortSignature<T> {
  Args: {
    actionNames?: string[];
    canToggleVisibility?: boolean;
    footerElement?: Element | null;
    items: Item<T>[];
    name?: string;
    onCancel: () => void;
    onReorder: (groupModel: Item<T>[]) => void;
    onSave: () => void;
    saveDisabled?: boolean;
    showName?: boolean;
  };
  Blocks: {
    default: [item: Item<T>, index: number];
  };
}

export default class GeneralModalsModalSort<T> extends Component<
  GeneralModalsModalSortSignature<T>
> {
  @service declare intl: Services['intl'];

  // used for certain display changes
  isDragActive = false;
  isLoading = false;

  @computed('args.items.length')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEmpty() {
    return this.args.items?.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get name() {
    return this.args.name ?? this.intl.t('general.name');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get saveDisabled() {
    return this.args.saveDisabled ?? true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showName() {
    return this.args.showName ?? true;
  }

  @action cancel(): void {
    this.args.onCancel();
  }

  /**
   * Sets the isDragActive property, this way we know any item is being dragged.
   * This is inly used for certain highlight display conditions
   */
  @action dragStarted(): void {
    set(this, 'isDragActive', true);
  }

  /**
   * Sets the isDragActive property, this way we know the item is not being dragged anymore.
   * This is only used for certain highlight display conditions
   */
  @action dragStopped(): void {
    set(this, 'isDragActive', false);
  }

  /**
   * Indicate the item is being hovered over, should not be set to true if the any item is being dragged
   * @param  { object } item : the item that is being hovered over
   */
  @action highlight(item: Item<T>): void {
    // if a drag is active, only the dragged element should be highlighted
    if (!this.isDragActive) {
      set(item, 'isHovering', true);
    }
  }

  @action onOrderChange(groupModel: Item<T>[]): void {
    this.args.onReorder(groupModel);
  }

  @action save(): void {
    this.args.onSave();
  }

  /**
   * remove the highlight
   * @param  { item } favourite : the item that the hover is leaving
   */
  @action undoHighlight(item: Item<T>): void {
    set(item, 'isHovering', false);
  }

  <template>
    <div class="sort-container">
      {{#if this.showName}}
        <div class="sort-header">
          <div class="handle"></div>
          <div class="position"></div>
          {{#if @canToggleVisibility}}
            <div class="visibility-toggle"></div>
          {{/if}}
          <div class="item-name truncate">
            {{this.name}}
          </div>
          {{#each @actionNames as |actionName|}}
            <div class="action-container truncate">
              {{actionName}}
            </div>
          {{/each}}
        </div>
      {{/if}}
      <div class="sort-body">
        {{#if this.isEmpty}}
          <div class="no-data-message-container">
            {{! @glint-expect-error: Property doesn't exist }}
            {{this.noDataMessage}}
          </div>
        {{else}}
          {{#if this.isLoading}}
            <PrismLoadingBar />
          {{/if}}
          {{#let "sort" as |groupName|}}
            <ul
              class="list-sort w-full"
              {{sortableGroup onChange=this.onOrderChange groupName=groupName}}
            >
              {{#each @items as |item index|}}
                <li
                  class={{if item.isDisabled "sortable-item--disabled"}}
                  data-test-id="modal-sort-item"
                  role="button"
                  {{on "mouseenter" (fn this.highlight item)}}
                  {{on "mouseleave" (fn this.undoHighlight item)}}
                  {{sortableItem
                    model=item
                    onDragStart=this.dragStarted
                    onDragStop=this.dragStopped
                    distance=5
                    groupName=groupName
                  }}
                >
                  {{yield item index}}
                </li>
              {{/each}}
            </ul>
          {{/let}}
        {{/if}}
      </div>
      {{#if @footerElement}}
        {{#in-element @footerElement}}
          <div class="flex flex-row gap-300">
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.cancel"}}
              @onClick={{this.cancel}}
              data-event-id="sort-modal-cancel"
            />
            <PrismButton
              @isDisabled={{this.saveDisabled}}
              @label={{t "general.save"}}
              @onClick={{this.save}}
              data-event-id="sort-modal-save"
              data-test-id="sort-modal-save"
            />
          </div>
        {{/in-element}}
      {{else}}
        <div class="sort-footer">
          <PrismButton
            @buttonType="secondary"
            @label={{t "general.cancel"}}
            @onClick={{this.cancel}}
            data-event-id="sort-modal-cancel"
          />
          <PrismButton
            @isDisabled={{this.saveDisabled}}
            @label={{t "general.save"}}
            @onClick={{this.save}}
            data-event-id="sort-modal-save"
            data-test-id="sort-modal-save"
          />
        </div>
      {{/if}}
    </div>
  </template>
}
